import React, { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, MessageSquare, Send } from 'lucide-react';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';

interface LogbookEntry {
  id: string;
  entry_type: string;
  epic_id: string | null;
  skill_name: string | null;
  verification_status: string | null;
  initiated_by: string | null;
  created_at: string;
  annotations?: Array<{ id: string; annotation: string; created_at: string }>;
}

const STATUS_COLORS: Record<string, string> = {
  pass: 'bg-emerald-900 text-emerald-200',
  fail: 'bg-red-900 text-red-200',
  start: 'bg-cyan-900 text-cyan-200',
  governance: 'bg-purple-900 text-purple-200',
};

export const LogbookView: React.FC = () => {
  const [entries, setEntries] = useState<LogbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [annotationText, setAnnotationText] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const url = filterType
        ? `${API_BASE}/api/logbook?limit=50&entry_type=${filterType}`
        : `${API_BASE}/api/logbook?limit=50`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: LogbookEntry[] = await res.json();
      setEntries(data);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load logbook');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, [filterType]);

  const fetchEntry = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/logbook/${id}`);
      if (!res.ok) return;
      const entry: LogbookEntry = await res.json();
      setEntries(prev => prev.map(e => e.id === id ? entry : e));
    } catch {}
  };

  const handleExpand = (id: string) => {
    if (expanded === id) {
      setExpanded(null);
    } else {
      setExpanded(id);
      fetchEntry(id);
    }
  };

  const handleAnnotate = async (entryId: string) => {
    const text = (annotationText[entryId] ?? '').trim();
    if (!text) return;
    setSaving(entryId);
    try {
      const res = await fetch(`${API_BASE}/api/logbook/${entryId}/annotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ annotation: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAnnotationText(prev => ({ ...prev, [entryId]: '' }));
      await fetchEntry(entryId);
    } catch (err: any) {
      setError(err.message ?? 'Failed to annotate');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100 mb-2">Audit Logbook</h1>
          <p className="text-slate-400">Immutable record of all skill invocations and governance events. Annotate entries for context.</p>
        </div>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="">All types</option>
          <option value="skill_start">skill_start</option>
          <option value="skill_pass">skill_pass</option>
          <option value="skill_fail">skill_fail</option>
          <option value="governance">governance</option>
        </select>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {loading ? (
        <div className="text-slate-400">Loading logbook...</div>
      ) : entries.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center">
          <BookOpen className="w-8 h-8 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400">No logbook entries yet. Entries are created automatically when skills run.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => (
            <div key={entry.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <button
                onClick={() => handleExpand(entry.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${STATUS_COLORS[entry.verification_status ?? entry.entry_type] ?? 'bg-slate-700 text-slate-300'}`}>
                    {entry.entry_type}
                  </span>
                  <span className="text-sm text-slate-100 truncate">
                    {entry.skill_name ?? entry.epic_id ?? entry.id}
                  </span>
                  {entry.epic_id && entry.skill_name && (
                    <span className="text-xs text-slate-500 shrink-0">{entry.epic_id}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-xs text-slate-400">{new Date(entry.created_at).toLocaleString()}</span>
                  {expanded === entry.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>

              {expanded === entry.id && (
                <div className="border-t border-slate-700 px-5 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {entry.epic_id && <div><span className="text-slate-500">Epic</span><p className="text-slate-200 font-mono mt-0.5">{entry.epic_id}</p></div>}
                    {entry.skill_name && <div><span className="text-slate-500">Skill</span><p className="text-slate-200 mt-0.5">{entry.skill_name}</p></div>}
                    {entry.verification_status && <div><span className="text-slate-500">Status</span><p className="text-slate-200 mt-0.5">{entry.verification_status}</p></div>}
                    {entry.initiated_by && <div><span className="text-slate-500">By</span><p className="text-slate-200 mt-0.5">{entry.initiated_by}</p></div>}
                  </div>

                  {/* Existing annotations */}
                  {entry.annotations && entry.annotations.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-400 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        Annotations
                      </p>
                      {entry.annotations.map(ann => (
                        <div key={ann.id} className="bg-slate-900/60 rounded p-3">
                          <p className="text-sm text-slate-200">{ann.annotation}</p>
                          <p className="text-xs text-slate-500 mt-1">{new Date(ann.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add annotation */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-2">Add annotation</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={annotationText[entry.id] ?? ''}
                        onChange={e => setAnnotationText(prev => ({ ...prev, [entry.id]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleAnnotate(entry.id)}
                        placeholder="Context, decision rationale, follow-up..."
                        disabled={saving === entry.id}
                        className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                      />
                      <button
                        onClick={() => handleAnnotate(entry.id)}
                        disabled={saving === entry.id || !(annotationText[entry.id] ?? '').trim()}
                        className="p-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 rounded-lg transition-colors text-white"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
